/* eslint-disable no-console */

import { Client, StompSocketState } from '@stomp/stompjs';
import SockJs from 'sockjs-client';

import configs from '@webtrader/core/configs';
import storage, { EStorageKey } from '@webtrader/core/storage';

import { chartService } from '../services/chart';
import ChartTopic from './topics/chart';
import MetaTraderTopic from './topics/metatrader';
import NewsTopic from './topics/news';
import PublicTopic from './topics/public';
import SignalTopic from './topics/signal';
import SymbolTopic from './topics/symbol';
import UserTopic from './topics/user';

const SOCKET_SERVER = `${configs.socketPathPrefix}/websocket`;

interface IWebsocketTopics {
  news: NewsTopic;
  signal: SignalTopic;
  symbol: SymbolTopic;
  public: PublicTopic;
  metatrader: MetaTraderTopic;
  chart: ChartTopic;
  user: UserTopic;
}

class WebSocketService {
  private client: Client;
  private socketTopics: IWebsocketTopics;
  private previouslyConnected = false;

  private onConnect?: (state?: StompSocketState) => void;
  private onDisconnect?: (state?: StompSocketState) => void;

  constructor() {
    this.client = new Client({
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      webSocketFactory: () => {
        console.log('[SOCKET_SERVER]', SOCKET_SERVER);

        return new SockJs(SOCKET_SERVER);
      },
      debug: () => {
        // console.log('[Socket Log]', message);
      },
      beforeConnect: () => {
        this.onReceiveState(this.client?.webSocket?.readyState);
      },
      onConnect: (payload) => {
        console.log('[onConnect]', payload);
        const state = this.client?.webSocket?.readyState;

        if (this.previouslyConnected) {
          this.socketTopics.symbol.reconnect();
          this.socketTopics.metatrader.reconnect();
          chartService?.refetchChartData();
        } else {
          this.previouslyConnected = true;
        }

        this.onConnect?.(state);
        this.onReceiveState(state);
      },
      onDisconnect: (payload) => {
        console.log('[onDisconnect]', payload);
        const state = this.client?.webSocket?.readyState;

        this.onDisconnect?.(state);
        this.onReceiveState(this.client?.webSocket?.readyState);
      },
      onStompError: (receipt) => {
        console.log('[onStompError]', receipt);
      },
      onWebSocketClose: () => {
        console.log('Web socket close');
        this.onReceiveState(this.client?.webSocket?.readyState);
      },
      onWebSocketError: () => {
        console.log('Web socket error');
        this.onReceiveState(this.client?.webSocket?.readyState);
      }
    });

    this.socketTopics = {
      news: new NewsTopic({
        client: this.client
      }),
      signal: new SignalTopic({
        client: this.client
      }),
      symbol: new SymbolTopic({
        client: this.client
      }),
      public: new PublicTopic({
        client: this.client
      }),
      metatrader: new MetaTraderTopic({
        client: this.client
      }),
      chart: new ChartTopic({
        client: this.client
      }),
      user: new UserTopic({
        client: this.client
      })
    };
  }

  public topics = () => {
    return this.socketTopics;
  };

  public state = () => {
    return this.client.state;
  };

  public connect = (onConnect?: (state?: StompSocketState) => void) => {
    if (!this.client) {
      return;
    }

    if (onConnect) {
      this.onConnect = onConnect;
    }

    const token = storage.getItem<string>(EStorageKey.TOKEN);

    if (token) {
      this.client.connectHeaders = {
        token
      };
    }

    this.client.activate();
  };

  public disconnect = (onDisconnect?: (state?: StompSocketState) => void) => {
    if (onDisconnect) {
      this.onDisconnect = onDisconnect;
    }

    this.previouslyConnected = false;

    this.clearSubscriptions();
    this.client.deactivate();
  };

  private clearSubscriptions = () => {
    const { socketTopics } = this;

    socketTopics.news.clearSubscriptions();
    socketTopics.signal.clearSubscriptions();
    socketTopics.symbol.clearSubscriptions();
    socketTopics.public.clearSubscriptions();
    socketTopics.metatrader.clearSubscriptions();
    socketTopics.user.clearSubscriptions();
  };

  private onReceiveState = (state?: StompSocketState) => {
    console.log('Received state:', state);
  };
}

export const socketService = new WebSocketService();
